import React from 'react';
import './ChartErrorBoundary.css';

/**
 * Error Boundary for TradingView Chart Component
 * Catches errors in chart initialization and rendering
 * Provides fallback UI with retry option
 */
class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('📊 Chart Error Boundary caught error:', error, errorInfo);
    
    this.setState({ errorInfo });
    
    // Send to error tracking service in production
    if (process.env.NODE_ENV === 'production' && window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack
          }
        }
      });
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="chart-error-boundary">
          <div className="chart-error-content">
            <div className="chart-error-icon">⚠️</div>
            <h2>Unable to Load Chart</h2>
            <p className="chart-error-message">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
              <details className="chart-error-details">
                <summary>Error Details (Dev Mode)</summary>
                <pre>{this.state.errorInfo.componentStack}</pre>
              </details>
            )}
            
            <div className="chart-error-actions">
              {this.state.retryCount < 3 && (
                <button 
                  onClick={this.handleRetry}
                  className="chart-error-button chart-error-button-primary"
                >
                  Retry ({3 - this.state.retryCount} attempts left)
                </button>
              )}
              <button 
                onClick={this.handleReload}
                className="chart-error-button chart-error-button-secondary"
              >
                Reload Page
              </button>
            </div>
            
            {this.state.retryCount >= 3 && (
              <p className="chart-error-help">
                If the problem persists, please contact support or try again later.
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChartErrorBoundary;
